import type {
  ContentListObligationsUnderInput,
  ContentListObligationsUnderOutput,
} from '@vivport/backend/types';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { executeTool } from '../api/tools-api';
import { contentAttachmentKeys } from './useContentAttachmentQueries';
import { contentKeys } from './useContentQueries';

export const obligationsUnderKeys = {
  all: ['obligations-under'] as const,
  byContent: (contentId: number) => [...obligationsUnderKeys.all, contentId] as const,
};

export const useObligationsUnder = (contentId: number, enabled: boolean) =>
  useQuery({
    queryKey: obligationsUnderKeys.byContent(contentId),
    queryFn: async (): Promise<ContentListObligationsUnderOutput> => {
      const response = await executeTool<ContentListObligationsUnderInput, ContentListObligationsUnderOutput>(
        'content.listObligationsUnder',
        { contentId }
      );
      if (response.status !== 'EXECUTED' || !response.output) {
        throw new Error(response.error ?? `content.listObligationsUnder ${response.status}`);
      }
      return response.output;
    },
    enabled,
    staleTime: 30_000,
  });

export function invalidateObligationViews(queryClient: QueryClient): void {
  const prefixes: ReadonlyArray<readonly unknown[]> = [
    obligationsUnderKeys.all,
    contentKeys.lists(),
    contentKeys.details(),
    [...contentKeys.all, 'documentRender'],
    contentAttachmentKeys.all,
    ['content-ref'],
  ];
  for (const queryKey of prefixes) {
    void queryClient.invalidateQueries({ queryKey });
  }
}
